import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '../../../ui/core';
import api from '../../../lib/api';

const EMPTY_SELECTION = { make: null, model: null, year: null };
const EMPTY_DETAILS = { licensePlate: '', color: null };
const EMPTY_ERRORS = { makes: false, models: false, years: false, colors: false };

const LoadError = ({ message, onRetry }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography sx={{ fontSize: '12px', color: 'error.main' }}>{message}</Typography>
    <Button size="small" onClick={onRetry} sx={{ minWidth: 0, textTransform: 'none' }}>
      Retry
    </Button>
  </Stack>
);

/** Omit empty instance facts so the server can distinguish absent from cleared. */
export const vehicleInput = (selection, details) => ({
  make: selection.make,
  model: selection.model,
  year: selection.year,
  ...Object.entries(details).reduce((present, [key, value]) => {
    const trimmed = value?.trim?.() || '';
    return trimmed ? { ...present, [key]: trimmed } : present;
  }, {}),
});

const AddVehicleDialog = ({ open, onClose, onAdd, busy }) => {
  const [options, setOptions] = useState({ makes: [], models: [], years: [], colors: [] });
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [loading, setLoading] = useState({ makes: false, models: false, years: false, colors: false });
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [submitting, setSubmitting] = useState(false);
  // Parent selections can change quickly; stale child responses must not win.
  const requests = useRef({ initial: 0, models: 0, years: 0 });

  const loadInitialOptions = useCallback(async () => {
    const requestId = ++requests.current.initial;
    setLoading((current) => ({ ...current, makes: true, colors: true }));
    setErrors((current) => ({ ...current, makes: false, colors: false }));
    const [makes, colors] = await Promise.allSettled([
      api.vehicles.getVehicleMakes(),
      api.vehicles.getVehicleColors(),
    ]);
    if (requests.current.initial === requestId) {
      setOptions((current) => ({
        ...current,
        makes: makes.status === 'fulfilled' ? makes.value : [],
        colors: colors.status === 'fulfilled' ? colors.value : [],
      }));
      setErrors((current) => ({
        ...current,
        makes: makes.status === 'rejected',
        colors: colors.status === 'rejected',
      }));
      setLoading((current) => ({ ...current, makes: false, colors: false }));
    }
  }, []);

  useEffect(() => {
    if (open) loadInitialOptions();
  }, [open, loadInitialOptions]);

  const selectMake = async (_, make) => {
    const requestId = ++requests.current.models;
    ++requests.current.years;
    setSelection({ make, model: null, year: null });
    setOptions((current) => ({ ...current, models: [], years: [] }));
    setErrors((current) => ({ ...current, models: false, years: false }));
    if (!make) {
      setLoading((current) => ({ ...current, models: false, years: false }));
      return;
    }
    setLoading((current) => ({ ...current, models: true }));
    try {
      const models = await api.vehicles.getVehicleModels(make);
      if (requests.current.models === requestId) setOptions((current) => ({ ...current, models }));
    } catch (e) {
      if (requests.current.models === requestId) setOptions((current) => ({ ...current, models: [] }));
      if (requests.current.models === requestId) setErrors((current) => ({ ...current, models: true }));
    } finally {
      if (requests.current.models === requestId) setLoading((current) => ({ ...current, models: false }));
    }
  };

  const selectModel = async (_, model) => {
    const requestId = ++requests.current.years;
    setSelection((current) => ({ ...current, model, year: null }));
    setOptions((current) => ({ ...current, years: [] }));
    setErrors((current) => ({ ...current, years: false }));
    if (!model) {
      setLoading((current) => ({ ...current, years: false }));
      return;
    }
    setLoading((current) => ({ ...current, years: true }));
    try {
      const years = await api.vehicles.getVehicleYears(selection.make, model);
      if (requests.current.years === requestId) setOptions((current) => ({ ...current, years }));
    } catch (e) {
      if (requests.current.years === requestId) setOptions((current) => ({ ...current, years: [] }));
      if (requests.current.years === requestId) setErrors((current) => ({ ...current, years: true }));
    } finally {
      if (requests.current.years === requestId) setLoading((current) => ({ ...current, years: false }));
    }
  };

  const reset = () => {
    ++requests.current.initial;
    ++requests.current.models;
    ++requests.current.years;
    setSelection(EMPTY_SELECTION);
    setOptions({ makes: [], models: [], years: [], colors: [] });
    setLoading({ makes: false, models: false, years: false, colors: false });
    setErrors(EMPTY_ERRORS);
    setDetails(EMPTY_DETAILS);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!selection.make || !selection.model || !selection.year || submitting || busy) return;
    setSubmitting(true);
    try {
      if (await onAdd(vehicleInput(selection, details))) close();
    } finally {
      setSubmitting(false);
    }
  };

  const working = busy || submitting;

  return (
    <Dialog open={open} onClose={working ? undefined : close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Add vehicle to driver</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mb: 2 }}>
          Choose an eligible catalog vehicle. Its insurance and registration requirements will be added to this
          driver’s record.
        </Typography>
        <Stack spacing={2}>
          <Autocomplete label="Make" options={options.makes} value={selection.make} loading={loading.makes} onChange={selectMake} />
          {errors.makes && <LoadError message="Couldn't load makes." onRetry={loadInitialOptions} />}
          <Autocomplete
            label="Model"
            options={options.models}
            value={selection.model}
            loading={loading.models}
            disabled={!selection.make}
            onChange={selectModel}
          />
          {errors.models && (
            <LoadError message="Couldn't load models." onRetry={() => selectMake(null, selection.make)} />
          )}
          <Autocomplete
            label="Year"
            options={options.years}
            value={selection.year}
            loading={loading.years}
            disabled={!selection.model}
            getOptionLabel={(year) => String(year)}
            onChange={(_, year) => setSelection((current) => ({ ...current, year }))}
          />
          {errors.years && (
            <LoadError message="Couldn't load years." onRetry={() => selectModel(null, selection.model)} />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="License plate"
              value={details.licensePlate}
              onChange={(e) => setDetails((current) => ({ ...current, licensePlate: e.target.value }))}
              size="small"
              fullWidth
            />
          </Stack>
          <Autocomplete
            label="Color"
            options={options.colors}
            value={details.color}
            loading={loading.colors}
            onChange={(_, color) => setDetails((current) => ({ ...current, color }))}
          />
          {errors.colors && <LoadError message="Couldn't load colors." onRetry={loadInitialOptions} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={working} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!selection.make || !selection.model || !selection.year || working}
          sx={{ textTransform: 'none', minWidth: 104 }}
        >
          {working ? <CircularProgress size={16} color="inherit" /> : 'Add vehicle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVehicleDialog;
